import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from 'xlsx';
import { getAttendanceList } from "../../../../../../app/store/slices/event/registration/registration";
import { errorToast } from "../../../../../../utils/toasts/toasts";

export default function useHandleAttendanceSearch() {
    const dispatch = useDispatch();
    const { eventData } = useSelector((state) => state.event);
    const {
        totalRegistrations,
        totalCheckIns,
        pendingCheckIns,
        attendance
    } = useSelector((state) => state.attendeeRegistration);

    const [originalData, setOriginalData] = useState(attendance || []);
    const [filteredData, setFilteredData] = useState(attendance || []);
    const [searchQuery, setSearchQuery] = useState('');

    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [refreshLoading, setRefreshLoading] = useState(false)
    const [error, setError] = useState(null);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    function handleSearchFilter(query) {
        setSearchQuery(query);
        const lowerCaseQuery = query.toLowerCase();
        const filtered = originalData.filter((item) => {
            return (
                item?.attendee_name.toLowerCase().includes(lowerCaseQuery) ||
                item?.attendee_email.toLowerCase().includes(lowerCaseQuery) ||
                item?.attendee_phone.includes(lowerCaseQuery) ||
                (item.ticket_number &&
                    item.ticket_number.toString().toLowerCase().includes(lowerCaseQuery))
            );
        });
        setFilteredData(filtered);
    }

    async function onRefresh() {
        try {
            if (!eventData || !eventData.eventId) return;

            setRefreshLoading(true);

            const response = await dispatch(getAttendanceList(eventData?.eventId)).unwrap();

            if (response?.success && response?.code === 200) {
                const registrations = response?.data?.records || [];
                setOriginalData(registrations);
                setFilteredData(registrations);
            }
        } catch (error) {
            setError(error);
        } finally {
            setRefreshLoading(false);
        }
    }

    useEffect(() => {
        if (attendance.length > 0) {
            setOriginalData(attendance);
            setFilteredData(attendance);
            setLoading(false);
        } else {
            if (attendance && attendance.length === 0) {
                setOriginalData([]);
                setFilteredData([]);
                setLoading(false);
            }
        }
    }, [attendance]);


    async function exportAttendanceList() {
        try {
            setExportLoading(true);

            const response = await dispatch(getAttendanceList(eventData?.eventId)).unwrap();

            if (response?.success && response?.code === 200) {
                const attendance = response?.data?.records || [];

                if (attendance.length === 0) {
                    errorToast("No attendance records available to export.");
                    return;
                }

                const rows = [];

                attendance.forEach((attendee) => {
                    const {
                        attendee_name,
                        attendee_email,
                        attendee_phone,
                        ticket_number,
                        ticket_type,
                        scanned_on,
                        scan_history,
                    } = attendee;

                    if (Array.isArray(scan_history) && scan_history.length > 0) {
                        // Grouped format: show attendee info only once, blank in subsequent rows
                        scan_history.forEach((history, index) => {
                            rows.push({
                                "Attendee Name": index === 0 ? attendee_name || "" : "",
                                "Email": index === 0 ? attendee_email || "" : "",
                                "Phone": index === 0 ? attendee_phone || "" : "",
                                "Ticket Name": index === 0 ? ticket_type || "" : "",
                                "Ticket Number": index === 0 ? ticket_number || "" : "",
                                "Scanned On": index === 0 ? scanned_on || "" : "",
                                "History ID": index + 1,
                                "Action": history.action === 'checked_in'
                                    ? "Checked In"
                                    : history.action === 'rejected'
                                        ? "Rejected"
                                        : history.action || "",
                                "Action Time": history.action_time || "",
                                "Checked In By": history.checked_in_by || "",
                                "Check-In Method": history.check_in_method || "",
                            });
                        });
                    }
                });

                // Create the Excel sheet and trigger download
                const worksheet = XLSX.utils.json_to_sheet(rows);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance List");

                XLSX.writeFile(workbook, `Attendance_List_${Date.now()}.csv`);
            }
        } catch (error) {
            errorToast("An error occurred");
        } finally {
            setExportLoading(false);
        }
    }


    return {
        originalData,
        filteredData,
        searchQuery,
        handleSearchFilter,
        loading,
        exportLoading,
        refreshLoading,
        onRefresh,
        error,
        exportAttendanceList,
        totalRegistrations,
        pendingCheckIns,
        totalCheckIns,
        setRowsPerPage,
        currentPage,
        setCurrentPage,
        rowsPerPage
    };
}